import { useNavigate } from 'react-router-dom'
import { CalendarCheck2, MessageSquareText, StickyNote, UserPlus } from 'lucide-react'
import { cn } from '@lib/utils'

type Tone = 'primary' | 'amber' | 'emerald' | 'slate'

interface Tile {
  label: string
  value: number
  sub: string
  icon: React.ElementType
  tone: Tone
  href: string
  progress?: number // 0..1
  alert?: boolean
}

const toneBar: Record<Tone, string> = {
  primary: 'bg-ml-primary',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-600',
  slate: 'bg-ml-on-surface-variant/40',
}
const toneIcon: Record<Tone, string> = {
  primary: 'text-ml-primary bg-ml-primary/10',
  amber: 'text-amber-700 bg-amber-100',
  emerald: 'text-emerald-700 bg-emerald-100',
  slate: 'text-ml-on-surface-variant bg-ml-surface-container-high',
}

interface KpiBandProps {
  isLoading: boolean
  appointmentsToday: number
  completedToday: number
  remainingToday: number
  pendingApprovals: number
  newPatientsThisWeek: number
  notesToday: number
}

export function KpiBand({ isLoading, appointmentsToday, completedToday, remainingToday, pendingApprovals, newPatientsThisWeek, notesToday }: KpiBandProps) {
  const navigate = useNavigate()
  const tiles: Tile[] = [
    {
      label: 'Citas de hoy', value: appointmentsToday,
      sub: appointmentsToday ? `${completedToday} atendidas · ${remainingToday} por atender` : 'Sin citas agendadas',
      icon: CalendarCheck2, tone: 'primary', href: '/agenda',
      progress: appointmentsToday ? completedToday / appointmentsToday : 0,
    },
    {
      label: 'Por confirmar', value: pendingApprovals,
      sub: pendingApprovals ? 'Solicitudes llegadas por WhatsApp' : 'Nada pendiente de tu OK',
      icon: MessageSquareText, tone: 'amber', href: '/agenda', alert: pendingApprovals > 0,
    },
    {
      label: 'Pacientes nuevos', value: newPatientsThisWeek,
      sub: 'Registrados esta semana', icon: UserPlus, tone: 'emerald', href: '/patients',
    },
    {
      label: 'Notas de recepción', value: notesToday,
      sub: notesToday ? 'Dejadas hoy por Scarlett y el equipo' : 'Ninguna hoy todavía',
      icon: StickyNote, tone: 'slate', href: '/patients',
    },
  ]

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
      {tiles.map((t) => (
        <button
          key={t.label}
          onClick={() => navigate(t.href)}
          className={cn(
            'group relative flex min-w-0 flex-col gap-3 overflow-hidden rounded-2xl border p-4 text-left transition sm:p-5',
            t.alert
              ? 'border-amber-300/70 bg-amber-50/70 hover:bg-amber-50'
              : 'border-ml-outline-variant/15 bg-ml-surface-container-lowest hover:border-ml-outline-variant/30 hover:shadow-sm',
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-ml-on-surface-variant/65">{t.label}</span>
            <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', toneIcon[t.tone])}>
              <t.icon className="h-4 w-4" />
            </span>
          </div>
          <span className={cn('font-headline text-4xl leading-none tabular-nums sm:text-5xl', t.alert ? 'text-amber-800' : 'text-ml-primary')}>
            {isLoading ? <span className="inline-block h-9 w-14 animate-pulse rounded bg-ml-surface-container" /> : t.value}
          </span>
          <span className="text-xs font-medium leading-snug text-ml-on-surface-variant/75">{t.sub}</span>
          {t.progress !== undefined && (
            <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-ml-surface-container-high">
              <span className={cn('block h-full rounded-full transition-all duration-700', toneBar[t.tone])} style={{ width: `${Math.round(t.progress * 100)}%` }} />
            </span>
          )}
        </button>
      ))}
    </div>
  )
}
